var Component     = require("enyo/Component"),
    findRootMedia = require("app/utils/findRootMedia"),
    get           = require("object-path").get,
    kind          = require("enyo/kind"),
    logger        = require("enyo/logger");

module.exports = kind(/** @lends  app.core.MediaPlayerManager.prototype */ {

    /**
     * @private
     */
    name: "app.core.MediaPlayerManager",

    /**
     * @private
     */
    kind: Component,

    /**
     * The activation promise
     *
     * @type  {Promise}
     * @private
     */
    mediaPlayerPromise: null,

    // Activation & Deactivation
    // --------------------------------

    /**
     * @type  {Function}
     * @private
     */
    mediaPlayerControllerKind: null,

    /**
     * @type  {app.core.MediaPlayerController}
     * @private
     */
    mediaPlayerController: null,

    /**
     * Activates the MediaPlayer.
     *
     * @param  {Object}   params                The parameters to be passed to
     *                                          the controller.
     * @param  {boolean}  isDocumentTitleOwner  Whether the MediaPlayer should
     *                                          control the document title
     * @public
     */
    activateMediaPlayer: function(params, isDocumentTitleOwner) {
        var mediaPlayerController = this.mediaPlayerController,
            mediaPlayerPromise;

        // Create a new controller instance if needed
        if (!mediaPlayerController) {
            mediaPlayerController      =  this.createComponent({
                kind:          this.mediaPlayerControllerKind,
                viewContainer: this.app.view.$.mediaPlayerRegion
            });
            this.mediaPlayerController = mediaPlayerController;
        }

        // Mark as documentTitleOwner as needed
        isDocumentTitleOwner && this.app.set("documentTitleOwner", mediaPlayerController);

        // Activate the media player
        mediaPlayerPromise = mediaPlayerController.activate(params);

        if (mediaPlayerPromise !== this.mediaPlayerPromise) {
            this.mediaPlayerPromise = mediaPlayerPromise;
            mediaPlayerPromise
                .then(this.bindSafely(function(callback) {
                    var errorMsg;

                    switch (mediaPlayerController.get("active")) {

                        // Media player was not activated. Discard it.
                        case -1:
                            this.deactivateMediaPlayer();
                            break;

                        // Media player was successfully activated.
                        case 1:
                            break;

                        // Improper usage
                        default:
                            errorMsg = "Media player's activation should be explicitly marked as succesfull or unsuccessful";
                            logger.warn(errorMsg);

                            if (typeof Sentry !== "undefined") {
                                Sentry.captureException(new Error(errorMsg), {
                                    extra: {
                                        active: mediaPlayerController.get("active")
                                    }
                                });
                            }
                            break;
                    }

                    // If a callback was supplied, we can execute it now.
                    callback && callback();
                }))
                .catch(this.bindSafely(function(error) {
                    logger.error(error);

                    if (typeof Sentry !== "undefined" && error instanceof Error) {
                        Sentry.captureException(error);
                    }

                    // Media player activation failed.
                    this.deactivateMediaPlayer();
                }));
        }
    },

    /**
     * Switches the MediaPlayer to the requested displayMode. If the player does
     * not support the requested mode, it may choose to disable itself.
     *
     * @param  {string}  displayMode  The display mode
     * @public
     */
    setMediaPlayerDisplayMode: function(displayMode) {
        var mediaPlayerController = this.mediaPlayerController;

        // No media player yet
        if (!mediaPlayerController) {
            this.app.$.state.set("mediaPlayerDisplayMode", displayMode);

            return;
        }

        // Set on MediaPlayerController
        mediaPlayerController.set("displayMode", displayMode);
    },

    /**
     * Deactivates the MediaPlayer
     *
     * @public
     */
    deactivateMediaPlayer: function() {
        var mediaPlayerController = this.mediaPlayerController;

        if (mediaPlayerController) {
            mediaPlayerController.deactivate();
        }
    },

    // Navigation
    // --------------------------------

    /**
     * Navigation to another mediaItem by the app itself without user
     * interaction.
     *
     * @param  {Object}  mediaItem  The mediaItem to navigate to
     * @public
     */
    autoNavToMediaItem: function(mediaItem) {
        var $appState   = this.app.$.state,
            $router     = this.app.$.router,
            displayMode = $appState.get("mediaPlayerDisplayMode"),
            language,
            mediaType,
            mediaSlug,
            artistSlug,
            playlistSlug,
            categorySlug,
            mediaLink,
            parts,
            queryString,
            queryParams,
            i,
            mediaPlayer;

        mediaLink = this.getMediaLink(mediaItem);

        if (!mediaLink) {
            return;
        }

        // Append 'cursor=0' parameter to mediaLink
        if (mediaLink.indexOf("?") === -1) {
            mediaLink += "?cursor=0";
        }
        else {
            parts       = mediaLink.split("?");
            queryString = parts[1];

            if (queryString.indexOf("cursor") === -1) {
                queryString += "&cursor=0";
            }
            else {
                queryParams = queryString.split("&");

                for (i = 0; i < queryParams.length; i++) {
                    if (queryParams[i].startsWith("cursor=")) {
                        queryParams[i] = "cursor=0";
                    }
                }

                queryString = queryParams.join("&");
            }

            mediaLink = parts.join("?");
        }

        // We want different behavior depending on the displayMode of the
        // player.
        switch (displayMode) {

            // Small player view, detached from the page the user is viewing.
            case "minimized":
            case "picture-in-picture":
                // The media player does not share the same context as the rest
                // of the content on the screen. Don't let it's content
                // influence the URL.
                language     = this.app.$.i18nService.get("language");
                mediaType    = get(mediaItem, "type", null);
                mediaSlug    = get(mediaItem, "meta.slug", null);
                artistSlug   = get(mediaItem, "playlistDetails.artistSlug", null);
                playlistSlug = get(mediaItem, "playlistDetails.playlistSlug", null);
                categorySlug = get(mediaItem, "playlistDetails.categorySlug", null);

                this.activateMediaPlayer({
                    language:     language,
                    mediaType:    mediaType,
                    mediaSlug:    mediaSlug,
                    artistSlug:   artistSlug,
                    playlistSlug: playlistSlug,
                    categorySlug: categorySlug,
                    cursor:       0
                }, false);

                return;

            // Small player view, integrated with the page the user is viewing.
            case "footer":
                // Same album/podcast/series/magazine
                if (this.pageRepresentsMediaItemRoot(mediaItem)) {
                    $router.redirect(mediaLink);

                    return;
                }

                // Same exact mediaItem
                if (this.pageRepresentsMediaItem(mediaItem)) {
                    $router.redirect(mediaLink);

                    return;
                }

                // Same artist and same playlist
                if (this.pageRepresentsMediaItemPlaylistArtist(mediaItem)
                    && this.pageContainsMediaItemPlaylist(mediaItem)) {
                    $router.redirect(mediaLink);

                    return;
                }

                // Same playlist
                if (this.pageRepresentsMediaItemPlaylist(mediaItem)) {
                    $router.redirect(mediaLink);

                    return;
                }

                // Same category
                if (this.pageRepresentsMediaItemCategory(mediaItem)) {
                    $router.redirect(mediaLink);

                    return;
                }

                // The media player does not share the same context as the rest
                // of the content on the screen. Don't let it's content
                // influence the URL.
                language     = this.app.$.i18nService.get("language");
                mediaType    = get(mediaItem, "type", null);
                mediaSlug    = get(mediaItem, "meta.slug", null);
                artistSlug   = get(mediaItem, "playlistDetails.artistSlug", null);
                playlistSlug = get(mediaItem, "playlistDetails.playlistSlug", null);
                categorySlug = get(mediaItem, "playlistDetails.categorySlug", null);

                this.activateMediaPlayer({
                    language:     language,
                    mediaType:    mediaType,
                    mediaSlug:    mediaSlug,
                    artistSlug:   artistSlug,
                    playlistSlug: playlistSlug,
                    categorySlug: categorySlug,
                    cursor:       0
                }, false);

                return;

            // Player has taken over the main view.
            case "maximized":
            default:
                // Same album/podcast/series/magazine
                if (this.playerSharesMediaItemRoot(mediaItem)) {
                    $router.redirect(mediaLink);

                    return;
                }

                // Same playlist
                if (this.playerSharesMediaItemPlaylist(mediaItem)) {
                    $router.redirect(mediaLink);

                    return;
                }

                // Same category
                if (this.playerSharesMediaItemCategory(mediaItem)) {
                    $router.redirect(mediaLink);

                    return;
                }

                // Already open, just start playing
                if (this.playerHasSameMediaItem(mediaItem)) {
                    if (!$appState.get("mediaPlayerPlaying")) {
                        mediaPlayer = $appState.get("mediaPlayer");

                        mediaPlayer
                            && mediaPlayer.play
                            && mediaPlayer.play();
                    }

                    return;
                }

                // Context switch
                $router.navigate(mediaLink);
                break;
        }
    },

    /**
     * Navigation to another mediaItem as the result of a user interaction.
     *
     * @param  {Object}  mediaItem  The mediaItem to navigate to
     * @public
     */
    userNavToMediaItem: function(mediaItem) {
        var $appState   = this.app.$.state,
            $router     = this.app.$.router,
            displayMode = $appState.get("mediaPlayerDisplayMode"),
            mediaLink,
            mediaPlayer;

        mediaLink = this.getMediaLink(mediaItem);

        if (!mediaLink) {
            return;
        }

        // We want different behavior depending on the displayMode of the
        // player.
        switch (displayMode) {

            // Small player view, detached from the page the user is viewing.
            case "minimized":
            case "picture-in-picture":
                $router.navigate(mediaLink);

                return;

            // Small player view, integrated with the page the user is viewing.
            case "footer":
                // Same album/podcast/series/magazine or same exact mediaItem
                if (this.pageRepresentsMediaItemRoot(mediaItem)
                    || this.pageRepresentsMediaItem(mediaItem)) {

                    // The player is already showing the item. Start playing it.
                    if (this.playerHasSameMediaItem(mediaItem)) {
                        if (!$appState.get("mediaPlayerPlaying")) {
                            mediaPlayer = $appState.get("mediaPlayer");

                            mediaPlayer
                                && mediaPlayer.play
                                && mediaPlayer.play();
                        }
                    }

                    // Player is showing another item, redirect to it.
                    else {
                        $router.redirect(mediaLink);
                    }

                    return;
                }

                // Same artist and same playlist
                if (this.pageRepresentsMediaItemPlaylistArtist(mediaItem)
                    && this.pageContainsMediaItemPlaylist(mediaItem)) {
                    $router.redirect(mediaLink);

                    return;
                }

                // Same playlist
                if (this.pageRepresentsMediaItemPlaylist(mediaItem)) {
                    $router.redirect(mediaLink);

                    return;
                }

                // Same category
                if (this.pageRepresentsMediaItemCategory(mediaItem)) {
                    $router.redirect(mediaLink);

                    return;
                }

                // The media player does not share the same context as the rest
                // of the content on the screen.
                $router.navigate(mediaLink);

                return;

            // Player has taken over the main view.
            case "maximized":
            default:
                // Same album/podcast/series/magazine
                if (this.playerSharesMediaItemRoot(mediaItem)) {
                    $router.redirect(mediaLink);

                    return;
                }

                // Same playlist
                if (this.playerSharesMediaItemPlaylist(mediaItem)) {
                    $router.redirect(mediaLink);

                    return;
                }

                // Same category
                if (this.playerSharesMediaItemCategory(mediaItem)) {
                    $router.redirect(mediaLink);

                    return;
                }

                // Already open, just start playing
                if (this.playerHasSameMediaItem(mediaItem)) {
                    if (!$appState.get("mediaPlayerPlaying")) {
                        mediaPlayer = $appState.get("mediaPlayer");

                        mediaPlayer
                            && mediaPlayer.play
                            && mediaPlayer.play();
                    }

                    return;
                }

                // Context switch
                $router.navigate(mediaLink);
                break;
        }
    },

    /**
     * Checks if the current page represents the mediaItem's root.
     *
     * @param    {Object}   mediaItem  The mediaItem to navigate to
     * @returns  {boolean}  `true` if check passed, `false` otherwise
     * @public
     */
    pageRepresentsMediaItemRoot: function(mediaItem) {
        var $appState   = this.app.$.state,
            pageMediaId = $appState.get("pageMediaId"),
            rootMedia   = mediaItem ? findRootMedia(mediaItem) : null,
            rootMediaId = get(rootMedia, "id", null);

        return pageMediaId
            && rootMediaId
            && pageMediaId === rootMediaId;
    },

    /**
     * Checks if the current page represents the mediaItem itself.
     *
     * @param    {Object}   mediaItem  The mediaItem to navigate to
     * @returns  {boolean}  `true` if check passed, `false` otherwise
     * @public
     */
    pageRepresentsMediaItem: function(mediaItem) {
        var $appState   = this.app.$.state,
            pageMediaId = $appState.get("pageMediaId"),
            mediaItemId = get(mediaItem, "id", null);

        return pageMediaId
            && mediaItemId
            && pageMediaId === mediaItemId;
    },

    /**
     * Checks current page represents the artist referenced in the mediaItem's
     * playlistDetails.
     *
     * @param    {Object}   mediaItem  The mediaItem to navigate to
     * @returns  {boolean}  `true` if check passed, `false` otherwise
     * @public
     */
    pageRepresentsMediaItemPlaylistArtist: function(mediaItem) {
        var $appState           = this.app.$.state,
            pageMediaType       = $appState.get("pageMediaType"),
            pageMediaSlug       = $appState.get("pageMediaSlug"),
            mediaItemArtistSlug = get(mediaItem, "playlistDetails.artistSlug", null);

        return pageMediaType === "artist_page"
            && pageMediaSlug
            && mediaItemArtistSlug
            && pageMediaSlug === mediaItemArtistSlug;
    },

    /**
     * Checks current page contains the playlist referenced in the mediaItem's
     * playlistDetails.
     *
     * @param    {Object}   mediaItem  The mediaItem to navigate to
     * @returns  {boolean}  `true` if check passed, `false` otherwise
     * @public
     */
    pageContainsMediaItemPlaylist: function(mediaItem) {
        var $appState             = this.app.$.state,
            pagePlaylistSlugs     = $appState.get("pagePlaylistSlugs") || [],
            mediaItemPlaylistSlug = get(mediaItem, "playlistDetails.playlistSlug", null);

        // Page contains the playlist
        return mediaItemPlaylistSlug
            && pagePlaylistSlugs.includes(mediaItemPlaylistSlug);
    },

    /**
     * Checks current page represents the playlist referenced in the mediaItem's
     * playlistDetails.
     *
     * @param    {Object}   mediaItem  The mediaItem to navigate to
     * @returns  {boolean}  `true` if check passed, `false` otherwise
     * @public
     */
    pageRepresentsMediaItemPlaylist: function(mediaItem) {
        var $appState             = this.app.$.state,
            pageMediaType         = $appState.get("pageMediaType"),
            pageMediaSlug         = $appState.get("pageMediaSlug"),
            mediaItemPlaylistSlug = get(mediaItem, "playlistDetails.playlistSlug", null);

        return pageMediaType === "playlist_page"
            && pageMediaSlug
            && mediaItemPlaylistSlug
            && pageMediaSlug === mediaItemPlaylistSlug;
    },

    /**
     * Checks current page represents the category referenced in the mediaItem's
     * playlistDetails.
     *
     * @param    {Object}   mediaItem  The mediaItem to navigate to
     * @returns  {boolean}  `true` if check passed, `false` otherwise
     * @public
     */
    pageRepresentsMediaItemCategory: function(mediaItem) {
        var $appState             = this.app.$.state,
            pageMediaType         = $appState.get("pageMediaType"),
            pageMediaSlug         = $appState.get("pageMediaSlug"),
            mediaItemCategorySlug = get(mediaItem, "playlistDetails.categorySlug", null);

        return pageMediaType === "category"
            && pageMediaSlug
            && mediaItemCategorySlug
            && pageMediaSlug === mediaItemCategorySlug;
    },

    /**
     * Checks if the currently playing mediaItem has the same root as the one we
     * supplied.
     *
     * @param    {Object}   mediaItem  The mediaItem to navigate to
     * @returns  {boolean}  `true` if same root, `false` otherwise
     * @private
     */
    playerSharesMediaItemRoot: function(mediaItem) {
        var $appState         = this.app.$.state,
            mediaPlayerRootId = $appState.get("mediaPlayerRootId"),
            rootMedia         = mediaItem ? findRootMedia(mediaItem) : null,
            rootMediaId       = get(rootMedia, "id", null);

        return mediaPlayerRootId
            && rootMediaId
            && mediaPlayerRootId === rootMediaId;
    },

    /**
     * Checks if the currently playing mediaItem came from the same playlist as
     * the one we supplied.
     *
     * @param    {Object}   mediaItem  The mediaItem to navigate to
     * @returns  {boolean}  `true` if same root, `false` otherwise
     * @private
     */
    playerSharesMediaItemPlaylist: function(mediaItem) {
        var $appState               = this.app.$.state,
            mediaPlayerPlaylistSlug = $appState.get("mediaPlayerPlaylistSlug"),
            mediaItemPlaylistSlug   = get(mediaItem, "playlistDetails.playlistSlug", null);

        return mediaPlayerPlaylistSlug
            && mediaItemPlaylistSlug
            && mediaPlayerPlaylistSlug === mediaItemPlaylistSlug;
    },

    /**
     * Checks if the currently playing mediaItem came from the same category as
     * the one we supplied.
     *
     * @param    {Object}   mediaItem  The mediaItem to navigate to
     * @returns  {boolean}  `true` if same category, `false` otherwise
     * @private
     */
    playerSharesMediaItemCategory: function(mediaItem) {
        var $appState               = this.app.$.state,
            mediaPlayerCategorySlug = $appState.get("mediaPlayerCategorySlug"),
            mediaItemCategorySlug   = get(mediaItem, "playlistDetails.categorySlug", null);

        return mediaPlayerCategorySlug
            && mediaItemCategorySlug
            && mediaPlayerCategorySlug === mediaItemCategorySlug;
    },

    /**
     * Checks if currently loading or played mediaItem is the same as the one we
     * supplied.
     *
     * @param    {Object}   mediaItem  The mediaItem to navigate to
     * @returns  {boolean}  `true` if same item, `false` otherwise
     * @public
     */
    playerHasSameMediaItem: function(mediaItem) {
        var $appState          = this.app.$.state,
            mediaPlayerReady   = $appState.get("mediaPlayerReady"),
            mediaPlayerMediaId = $appState.get("mediaPlayerMediaId"),
            mediaItemId        = get(mediaItem, "id", null);

        return mediaPlayerMediaId
            && mediaItemId
            && (mediaPlayerReady === 0 || mediaPlayerReady === 1)
            && mediaPlayerMediaId === mediaItemId;
    },

    /**
     * Resolve the link for the MediaPlayer for the supplied MediaItem, given
     * the current displayMode of the MediaPlayer.
     *
     * @param    {Object}   mediaItem  The mediaItem to navigate to
     * @returns  {string?}  The link if found, `null` otherwise
     * @private
     */
    getMediaLink: function(mediaItem) {
        var $appState   = this.app.$.state,
            displayMode = $appState.get("mediaPlayerDisplayMode");

        switch (displayMode) {
            case "maximized":
                return get(mediaItem, "links.expandedPlayer", null)
                    || get(mediaItem, "links.player", null);
            case "minimized":
            case "picture-in-picture":
            case "footer":
            default:
                return get(mediaItem, "links.player", null);
        }
    }

});
